import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';
import '../ui/dropdown-menu.js';

/**
 * Render-scale control: a compact trigger button that opens a <dropdown-menu>
 * popover containing a range slider covering 25–200%. The slider stays large
 * enough to drag with a finger on iPad, but is hidden until the button is
 * tapped so the footer stays compact.
 *
 * @fires scale-change - detail: { scale: number } (1.0 = 100%)
 */
@customElement('scale-control')
export class ScaleControl extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: inline-block;
            }

            dropdown-menu {
                --dropdown-min-width: 240px;
                --dropdown-padding: 12px;
            }

            /* Compact trigger button — matches settings-panel's .settings-btn */
            .scale-btn {
                display: flex;
                align-items: center;
                gap: 0.4rem;
                background: transparent;
                border: 1px solid var(--border-color, #475569);
                border-radius: 6px;
                color: var(--text-secondary, #cbd5e1);
                font-size: 0.85rem;
                font-weight: 500;
                padding: 0.5rem 0.75rem;
                cursor: pointer;
                transition: all 0.2s;
                white-space: nowrap;
            }

            .scale-btn:hover {
                background: var(--surface-light, rgba(255, 255, 255, 0.1));
                color: var(--text-primary, #f1f5f9);
            }

            .container {
                display: flex;
                align-items: center;
                gap: 0.5rem;
            }

            .scale-icon {
                font-size: 1rem;
                line-height: 1;
                user-select: none;
                -webkit-user-select: none;
            }

            .scale-slider {
                width: 200px;
                height: 8px;
                -webkit-appearance: none;
                appearance: none;
                background: #555;
                border-radius: 4px;
                cursor: pointer;
                margin: 0;
            }

            .scale-slider::-webkit-slider-thumb {
                -webkit-appearance: none;
                width: 28px;
                height: 28px;
                background: #fff;
                border-radius: 50%;
                cursor: pointer;
                border: none;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
            }

            .scale-slider::-moz-range-thumb {
                width: 28px;
                height: 28px;
                background: #fff;
                border-radius: 50%;
                cursor: pointer;
                border: none;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
            }

            .scale-label {
                font-family: monospace;
                font-size: 0.85rem;
                color: white;
                min-width: 3.25rem;
                text-align: right;
                user-select: none;
                -webkit-user-select: none;
            }
        `,
    ];

    @property({ type: String })
    prefix: 'conductor' | 'player' = 'conductor';

    @property({ type: Number })
    scale = 1.0;

    @property({ type: Number })
    min = 0.25;

    @property({ type: Number })
    max = 2.0;

    @property({ type: Number })
    step = 0.05;

    private _rafPending = false;
    private _pendingScale: number | null = null;

    connectedCallback(): void {
        super.connectedCallback();
        const saved = localStorage.getItem(`${this.prefix}Scale`);
        if (saved !== null) {
            const parsed = parseFloat(saved);
            if (Number.isFinite(parsed)) {
                this.scale = Math.max(this.min, Math.min(this.max, parsed));
            }
        }
    }

    private _onInput(e: Event): void {
        const raw = parseFloat((e.target as HTMLInputElement).value);
        if (!Number.isFinite(raw)) return;
        const clamped = Math.max(this.min, Math.min(this.max, raw));
        this.scale = clamped;
        localStorage.setItem(`${this.prefix}Scale`, String(clamped));
        this._pendingScale = clamped;
        if (!this._rafPending) {
            this._rafPending = true;
            requestAnimationFrame(() => {
                this._rafPending = false;
                if (this._pendingScale === null) return;
                const scale = this._pendingScale;
                this._pendingScale = null;
                this.dispatchEvent(
                    new CustomEvent('scale-change', {
                        bubbles: true,
                        composed: true,
                        detail: { scale },
                    })
                );
            });
        }
    }

    render() {
        const pct = Math.round(this.scale * 100);
        return html`
            <dropdown-menu position="top-right">
                <button slot="trigger" class="scale-btn" title="Zoom" aria-label="Zoom">
                    <span class="scale-icon">${'🔍'}</span>
                </button>
                <div class="container">
                    <input
                        type="range"
                        class="scale-slider"
                        min=${String(this.min)}
                        max=${String(this.max)}
                        step=${String(this.step)}
                        .value=${String(this.scale)}
                        @input=${this._onInput}
                    >
                    <span class="scale-label">${pct}%</span>
                </div>
            </dropdown-menu>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'scale-control': ScaleControl;
    }
}
